import { CLASS_IDS, classColor } from "@eldrun/content";
import { MatchState, PlayerInfo } from "@eldrun/protocol";
import { parseSeed } from "@eldrun/worldgen";
import type { MatchSession } from "../net/match-client.ts";
import { gameColor } from "../render/style/roles.ts";
import { IslandLayers, IslandMap } from "../ui/panels/island-map.ts";
import { FogOfWar, mapMarkers } from "../ui/panels/map-model.ts";
import { sheetView } from "../ui/panels/sheet-model.ts";
import { SheetPanel } from "../ui/panels/sheet-panel.ts";
import { ClassSelect } from "../ui/screens/class-select.ts";
import { renderLobby } from "../ui/screens/lobby.ts";
import { TerrainWorkerPool, wrapWorker } from "../world/worker-pool.ts";

/** A vitrine põe cada jogador na classe do índice dele (F85: a cor é a da classe). */
const classIndexColor = (slot: number) => gameColor(classColor(CLASS_IDS[slot] ?? ""));

/**
 * Mapa da vitrine (F82, `?debug=ui&painel=mapa`): o plano da seed SACI (só o planejador, num worker), a
 * névoa aberta num caminho e marcadores de exemplo (você, dois aliados, pings, um chefe, uma Lápide, os
 * lugares descobertos), com um ping escolhido no detalhe.
 */
export async function showcaseMap(parent: HTMLElement, slot: number): Promise<void> {
  const pool = new TerrainWorkerPool(() => wrapWorker(new Worker(new URL("../world/terrain.worker.ts", import.meta.url), { type: "module" })), 1);
  const { plan } = await pool.plan(parseSeed("SACI"), "mutirao");
  pool.terminate();
  const map = new IslandMap(parent, new IslandLayers(plan));
  const anchor = plan.pois.find((p) => p.type === "carranca") ?? plan.pois[0] ?? { x: 0, z: 0 };
  const self = { x: anchor.x + 40, z: anchor.z + 30, yaw: 0.6 };
  const fog = new FogOfWar();
  // Um caminho explorado em volta de você e até os lugares mais perto.
  const near = [...plan.pois].sort((a, b) => Math.hypot(a.x - self.x, a.z - self.z) - Math.hypot(b.x - self.x, b.z - self.z)).slice(0, 12);
  for (const p of [self, ...near]) {
    const steps = Math.ceil(Math.hypot(p.x - self.x, p.z - self.z) / 40);
    for (let i = 0; i <= steps; i++) fog.reveal(self.x + ((p.x - self.x) * i) / Math.max(1, steps), self.z + ((p.z - self.z) * i) / Math.max(1, steps));
  }
  const others = [0, 1, 2, 3].filter((s) => s !== slot);
  const { markers, circles } = mapMarkers({
    self,
    allies: [
      { name: "Bia", color: classIndexColor(others[0]!), n: others[0]! + 1, x: self.x - 70, z: self.z - 50, dead: false },
      { name: "Caio", color: classIndexColor(others[1]!), n: others[1]! + 1, x: self.x + 120, z: self.z + 60, dead: true },
    ],
    pings: [
      { x: self.x + 90, z: self.z - 80, kind: "perigo", name: "Bia", color: classIndexColor(others[0]!) },
      { x: self.x - 40, z: self.z + 110, kind: "recurso", name: "Você", color: classIndexColor(slot) },
    ],
    pois: plan.pois,
    revealed: (x, z) => fog.revealed(x, z),
    raftKnown: true,
    guardiansKnown: true,
    raft: undefined,
    crosses: [{ x: self.x + 120, z: self.z + 60, name: "Caio" }],
    whirlwinds: [],
    bosses: [{ x: self.x + 260, z: self.z - 180, name: "Ciclope", id: "c1" }],
    cerco: undefined,
    paint: gameColor,
  });
  map.toggle(true, self);
  map.view.zoom = 1.6;
  map.select(markers.find((m) => m.kind === "ping")!.id);
  const draw = () => map.draw(fog, markers, circles);
  draw();
  setInterval(draw, 100);
  await new Promise((resolve) => requestAnimationFrame(resolve));
}

/** Ficha da vitrine (F82, `?debug=ui&painel=ficha`): a classe do índice pedido no meio de uma partida. */
export function showcaseSheet(parent: HTMLElement, slot: number): void {
  const sheet = new SheetPanel(parent);
  sheet.toggle(true);
  sheet.update(
    sheetView({
      name: "Ana",
      classId: CLASS_IDS[slot] ?? "",
      slot,
      color: classIndexColor(slot),
      mode: "mutirao",
      vitals: { hp: 82, hpMax: 112, patua: 18, patuaMax: 25, hunger: 71, hungerMax: 100 },
      stamina: { value: 64, max: 100 },
      simpatias: [["fita-chita", 1], ["guarana", 2], ["trovoada", 1]],
      armor: ["capacete-couro", "gibao-couro", "", ""],
      oaths: ["brado-amplo", "investida-feroz", "runa-ampla", "sinal-amplo"][slot] ?? "",
      ultCharge: 64,
      activeLeft: 4,
      keys: { ativa: "Botão direito", ultimate: "V" },
      stats: { kills: 14, deaths: 1, revives: 2, dealt: 1840, taken: 420, simpatias: 4, chests: 3, coins: 140, raft: 0, whirlwinds: 0, pranks: 1, blocked: 210, healed: 0, elos: 6 },
    }),
  );
}

/**
 * Lobby da vitrine (F82, `?debug=ui&painel=lobby`): uma sala privada de exemplo (o estado de verdade do
 * protocolo, sem servidor) com três jogadores; você com a classe do índice pedido, e Bia e Caio na mesma
 * classe (F85: dividem a cor e se distinguem pelo número).
 */
export function showcaseLobby(container: HTMLElement, slot: number): void {
  const state = new MatchState();
  state.code = "SACI7";
  state.isPrivate = true;
  state.ownerId = "eu";
  state.seedCode = "SACI";
  const others = [0, 1, 2, 3].filter((s) => s !== slot);
  const players: [string, string, number, string, boolean][] = [
    ["eu", "Ana", slot, CLASS_IDS[slot] ?? "", true],
    ["b", "Bia", others[0]!, "lanceiro", true],
    ["c", "Caio", others[1]!, "lanceiro", false],
  ];
  for (const [id, name, n, classId, ready] of players) {
    const p = new PlayerInfo();
    p.sessionId = id;
    p.name = name;
    p.slot = n;
    p.classId = classId;
    p.ready = ready;
    state.players.set(id, p);
  }
  const noop = () => {};
  const session = { state, sessionId: "eu", configure: noop, chooseClass: noop, kick: noop, start: noop, setReady: noop } as unknown as MatchSession;
  const select = new ClassSelect({ mode: "lobby", onChoose: noop, onBack: noop });
  renderLobby(container, session, noop, select);
  select.start(container);
}
